import { LoansPaths } from 'app/configs/routesConfig/loans';
import dayjs from 'dayjs';
import { isNil } from 'lodash';

import { SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';
import { getConditionalObject } from 'utils/data';
import { formatLockExpirationDate } from 'utils/lockExpiration';

import {
  ACTION_ITEM_CATEGORY_TO_TAB,
  ACTION_ITEM_SEVERITY_BY_API,
  ACTION_ITEM_URGENCY_RANK,
  ACTION_ITEMS_FRESHNESS_PREFIX,
  LOCK_EXPIRY_CATEGORY,
} from './actionItems.constants';
import {
  AlertSeverity,
  COMMAND_CENTER_ATTENTION_PREVIEW_COUNT,
} from './CommandCenterHome.constants';

const resolveSeverity = severity =>
  ACTION_ITEM_SEVERITY_BY_API[severity] ?? AlertSeverity.INFORMATIONAL;

// Renders a borrower last name + loan last-4 into a single PII-minimized label.
// Last-4 is always present from the backend; the name is optional.
const composeSubjectLabel = ({ borrowerLastName, loanNumberLast4 }) => {
  const loanFragment = isNil(loanNumberLast4) ? '' : `·${loanNumberLast4}`;

  if (isNil(borrowerLastName)) {
    return loanFragment;
  }

  return `${borrowerLastName} ${loanFragment}`.trim();
};

const mapActionItem = item => ({
  id: item.id,
  severity: resolveSeverity(item.severity),
  category: item.category,
  applicationId: item.applicationId ?? null,
  message: item.message,
  subjectLabel: composeSubjectLabel(item),
});

// Builds an action row from a lock-expiry record so the lender's lock book and
// the broker action items share one urgency-sorted queue. Display-only — the
// extension cost stays in Pricing.
const mapLockToActionItem = lock => {
  const expiresOn = isNil(lock.lockExpirationDate)
    ? null
    : formatLockExpirationDate(lock.lockExpirationDate, SHORT_MONTH_DAY_YEAR_FORMAT);

  return {
    id: `lock-${lock.applicationId}`,
    severity: AlertSeverity.ACTION_REQUIRED,
    category: LOCK_EXPIRY_CATEGORY,
    applicationId: lock.applicationId ?? null,
    message: isNil(expiresOn) ? 'Lock expiring soon' : `Lock expires ${expiresOn}`,
    subjectLabel: composeSubjectLabel(lock),
  };
};

const sortByUrgency = (left, right) =>
  (ACTION_ITEM_URGENCY_RANK[left.severity] ?? Number.MAX_SAFE_INTEGER) -
  (ACTION_ITEM_URGENCY_RANK[right.severity] ?? Number.MAX_SAFE_INTEGER);

const getActionItemsFreshnessLabel = asOf => {
  if (isNil(asOf)) {
    return null;
  }

  return `${ACTION_ITEMS_FRESHNESS_PREFIX} ${dayjs(asOf).format(SHORT_MONTH_DAY_YEAR_FORMAT)}`;
};

/**
 * Merges the broker action-items endpoint with the lock-expiry endpoint into a
 * single urgency-sorted display queue. Returns null when neither responded so the
 * reducer holds its empty state.
 * @param {object|null} actionItems - { items, asOf }
 * @param {object|null} lockExpiry - { locks, windowDays, asOf }
 * @returns {object|null}
 */
export const mapCommandCenterActionItems = ({ actionItems, lockExpiry }) => {
  if (isNil(actionItems) && isNil(lockExpiry)) {
    return null;
  }

  const items = (actionItems?.items ?? []).map(mapActionItem);
  const locks = (lockExpiry?.locks ?? []).map(mapLockToActionItem);

  const merged = [...locks, ...items].sort(sortByUrgency);
  const asOf = actionItems?.asOf ?? lockExpiry?.asOf ?? null;

  return {
    items: merged,
    asOf,
    freshnessLabel: getActionItemsFreshnessLabel(asOf),
  };
};

/**
 * Returns the top N items from the urgency-sorted list.
 * @param {Array} items
 * @param {number} count
 * @returns {Array}
 */
export const getTopActionItems = (items, count = COMMAND_CENTER_ATTENTION_PREVIEW_COUNT) =>
  items.slice(0, count);

/**
 * Returns only the lock-expiry items (LOCK_EXPIRY_CATEGORY) from the sorted list.
 * Used by ActionCenterCard to show a focused lock-expiry zone.
 * @param {Array} items
 * @returns {Array}
 */
export const getLockExpiryItems = (items = []) =>
  items.filter(item => item.category === LOCK_EXPIRY_CATEGORY);

/**
 * Returns only the non-lock-expiry broker action items from the sorted list.
 * Used by the StatusZone ActionQueue to show conditions, doc tasks, etc.
 * @param {Array} items
 * @returns {Array}
 */
export const getBrokerActionItems = (items = []) =>
  items.filter(item => item.category !== LOCK_EXPIRY_CATEGORY);

/**
 * DEV-4168: resolves an action-item category to its loan-management main-tab slug,
 * or null when the category has no verified slug (the caller then lands on the
 * loan's default tab). Never returns a guessed slug — a wrong slug 404s.
 * @param {string} category
 * @returns {string|null}
 */
export const getActionItemTabSlug = category => ACTION_ITEM_CATEGORY_TO_TAB[category] ?? null;

/**
 * DEV-4168: builds the loan-management deep-link path for an action item, landing
 * on the tab matching the item category when one is mapped and falling back to the
 * default-tab landing otherwise. Returns null when there is no application to open.
 * @param {{ applicationId: string|null, category: string }} actionItem
 * @returns {string|null}
 */
export const getActionItemDeepLinkPath = ({ applicationId, category }) => {
  if (isNil(applicationId)) {
    return null;
  }

  const tab = getActionItemTabSlug(category);

  return LoansPaths.applicationManagement.generatePath({
    applicationId,
    ...getConditionalObject(!isNil(tab), () => ({ tab })),
  });
};
